import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import SearchResultGrid from '../searchResultGrid/searchResultGrid';
import apiClient from '../../axiosClient';
import Case from '../../interfaces/case';
import { caseColumnDef, caseColumnGroup } from '../../colDef/caseColumnDef';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { currentPoolMonth, debounce, decodedJWT, getOwnerType, handleSearchCase, isGridLeafColumn } from '../../utils/commonUtils';
import ToolbarContainer from '../baseComponents/toolbarContainer';
import Filter from '../../interfaces/filters';
import FilterDrawer from './filterDrawer';
import JWTTokenPayload from '../../interfaces/JWTTokenPayload';
import RoleManagement from '../../security/roleManagement';
import { createFilters, getSelectedFiltersCount } from '../../utils/filterUtils';
import SeeDetailsDrawer from '../modals/seeDetailsDrawer';
import { loadPredictiveCases } from '../../store/slices/predictiveCaseSlice';

const agencyFilters = ['interaction_type', 'focused', 'pin_code', 'dpd_bucket', 'client_name', 'loan_type', 'status', 'segment_id', 'l0_interaction', 'state', 'district', 'principal_outstanding_amt', 'total_due_amt'];
const clientFilters = ['interaction_type', 'client_manager_username', 'pin_code', 'dpd_bucket', 'agency_name', 'loan_type', 'status', 'l0_interaction', 'state', 'district', 'principal_outstanding_amt', 'total_due_amt'];

const MyAllocation: React.FC = () => {
	const dispatch: AppDispatch = useDispatch();
	const token: JWTTokenPayload | false = decodedJWT();
	const ownerType = getOwnerType(token);
	const segments = useSelector((state: RootState) => state.segments.segments);
	const clients = useSelector((state: RootState) => state.clients.clients);
	const agencies = useSelector((state: RootState) => state.agencies.agencies);
	const picklist = useSelector((state: RootState) => state.picklists.picklists);
	const districtAndStates = useSelector((state: RootState) => state.districtsAndStates);
	const states = districtAndStates.states;
	const districts = districtAndStates.districts.map(val => val.district);
	const userSlice = useSelector((state: RootState) => state.userList);
	const { gridSelectedCase } = useSelector((state: RootState) => state.gridSelectedCase);
	const users = userSlice.users;
	const clientManagerList = userSlice.clientManagerDescendants;

	const [filterResults, setFilterResults] = useState<Case[]>([]);
	const [leftMarginDashboard, setLeftMarginDashboard] = useState<string>('16px');
	const [loading, setLoading] = useState<boolean>(false);
	const [paginationModel, setPaginationModel] = useState<{ page: number, pageSize: number }>({ page: 0, pageSize: 40 });
	const [rowCount, setRowCount] = useState<number>(0);
	const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
	const [filters, setFilters] = useState<Filter[]>([]);
	const [universalSearchQuery, setUniversalSearchQuery] = useState<string>("");
	const [selectedPoolMonth, setSelectedPoolMonth] = useState<string>(currentPoolMonth()); // Store month in parent
	const [groupState, setGroupState] = React.useState<Record<string, { visible: boolean; button: boolean; }>>({
		'Case Data': { visible: true, button: false },
		'Latest Interaction': { visible: false, button: true },
		'Best Interaction': { visible: false, button: true },
		'Latest Call Interaction': { visible: false, button: true },
		'Best Call Interaction': { visible: false, button: true },
		'Latest Visit Interaction': { visible: false, button: true },
		'Best Visit Interaction': { visible: false, button: true },
		'Pincode Data': { visible: true, button: false },
		'Timestamps': { visible: true, button: false },
	});
	const [predictiveDialerModal, setPredictiveDialer] = useState<boolean>(false);

	const visibleColumns = React.useMemo(() => {
		const result = [];
		for (const groupKey of caseColumnGroup) {
			const cols = groupKey.children.map(val => {
				if (isGridLeafColumn(val)) {
					return val.field;
				}
				return "";
			});
			result.push(...(groupState[groupKey.groupId].visible ? cols : [cols[0]]));
		}
		const visibleColumnsSet: Set<string> = new Set(result);
		return caseColumnDef.filter((col) => visibleColumnsSet.has(col.field));
	}, [groupState]);

	const toggleGroup = (groupKey: string) => {
		setGroupState((prev) => ({
			...prev,
			[groupKey]: { visible: !prev[groupKey].visible, button: prev[groupKey].button },
		}));
	};

	const clearResults = () => {
		setFilterResults([]);
		setRowCount(0);
	};

	const setDefaultFilters = () => {
		const finalFilters = createFilters(
			RoleManagement.isAgency(ownerType) ? agencyFilters : clientFilters, segments.filter(val => val.is_assigned_to_user), clients, agencies, picklist, states, districts, users, clientManagerList
		);
		setFilters(finalFilters);
	};


	useEffect(() => {
		setDefaultFilters();
	}, [clients]);

	const debouncedSearch = useMemo(() => debounce(handleSearchCase, 500), [paginationModel, selectedPoolMonth, universalSearchQuery, filters]);

	useEffect(() => {
		debouncedSearch.cancel(); // for safety don't in case the reference to the last function is lost and is not created again then this will be useful
		debouncedSearch(filters, universalSearchQuery, paginationModel.page, selectedPoolMonth, false, segments, "/api/v1/agent_case_allocation/combine_allocation", setLoading, clearResults, setRowCount, setFilterResults);
		return () => {
			debouncedSearch.cancel();
		};
	}, [paginationModel, universalSearchQuery, filters, selectedPoolMonth]);

	const toggleDrawer = () => {
		setDrawerOpen(!drawerOpen);
		if (!drawerOpen) {
			setLeftMarginDashboard('166px');
		} else {
			setLeftMarginDashboard('16px');
		}
	};

	const resetFilters = () => {
		setDefaultFilters();
		setPaginationModel({ page: 0, pageSize: 40 });
	};

	const fetchPredictiveCases = async () => {
		try {
			const response = await apiClient.get(`/api/v1/predictive_cases`);
			if (response.status === 200) {
				dispatch(loadPredictiveCases(response.data));
				setPredictiveDialer(true);
			}
		} catch (error: any) {
			if (error.response) {
				alert('Error response data:' + error.response.data.message);
			} else if (error.request) {
				alert('No response received:' + error.request);
			} else {
				alert('Error message:' + error.message);
			}
		}
	};

	return (
		<>
			<Box sx={{ ml: leftMarginDashboard, mr: 2 }}>
				<FilterDrawer
					filters={filters}
					setFilters={setFilters}
					drawerOpen={drawerOpen}
					toggleDrawer={toggleDrawer}
				/>
				<ToolbarContainer
					setFilters={setFilters}
					toggleDrawer={toggleDrawer}
					filtersCount={getSelectedFiltersCount(filters)}
					universalSearchQuery={universalSearchQuery}
					setUniversalSearchQuery={setUniversalSearchQuery}
					selectedPoolMonth={selectedPoolMonth}
					setSelectedPoolMonth={setSelectedPoolMonth}
					resetFilters={resetFilters}
				/>
				{loading ? (
					<Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
						<CircularProgress color="primary" />
					</Box>
				) : (
					<SearchResultGrid
						height='70vh'
						disableFilter={true}
						disableSorting={true}
						disableAsecDescSorting={true}
						getRowId={(row) => row.case_id}
						results={filterResults}
						onSelectRow={() => {}}
						columns={visibleColumns}
						rowCount={rowCount}
						paginationModel={paginationModel}
						setPaginationModel={setPaginationModel}
						columnGroupModel={caseColumnGroup}
						showToolBar={false}
						groupVisibilityMap={groupState}
						toggleGroupVisibility={toggleGroup}
					/>
				)}
			</Box>
			<SeeDetailsDrawer/>
		</>
	);
};

export default MyAllocation;